import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { createIdbKvStore } from '../storage/idb-kv-store.util';

/** Persisted consent state. `null` = the visitor hasn't decided yet
 *  (banner should show); `true` = explicitly accepted non-essential
 *  cookies; `false` = explicitly declined. */
type ConsentChoice = boolean | null;

const STORAGE_KEY = 'cnt-cookie-consent';
const STORAGE_IDB_KEY = 'consent:cookie';

/** Tracks whether the visitor has dismissed the cookie banner and
 *  whether they accepted or declined non-essential cookies (analytics,
 *  etc.). The banner gates on `hasDecided$`; any future analytics
 *  loader gates on `accepted$`. Persisted so the banner doesn't
 *  re-appear on every page load. */
@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private platformId = inject(PLATFORM_ID);

  private readonly store = createIdbKvStore<boolean>({
    legacyKeys: { [STORAGE_IDB_KEY]: STORAGE_KEY },
    reviveLegacy: (_key, parsed) =>
      parsed === true || parsed === 'true' ? true : parsed === false || parsed === 'false' ? false : undefined,
  });

  private readonly _choice$ = new BehaviorSubject<ConsentChoice>(null);

  readonly hasDecided$: Observable<boolean> = this._choice$.pipe(map((c) => c !== null));
  readonly accepted$: Observable<boolean> = this._choice$.pipe(map((c) => c === true));

  constructor() {
    this._choice$.next(this.read());
    if (isPlatformBrowser(this.platformId)) {
      void this.store.init().then(() => {
        const choice = this.read();
        // Native app shell has no cookies and shows no banner — treat an
        // undecided visitor as accepted so analytics still loads.
        if (choice === null && Capacitor.isNativePlatform()) {
          this.write(true);
        } else {
          this._choice$.next(choice);
        }
      });
    }
  }

  hasDecided(): boolean {
    return this._choice$.value !== null;
  }

  accepted(): boolean {
    return this._choice$.value === true;
  }

  accept(): void {
    this.write(true);
  }
  decline(): void {
    this.write(false);
  }
  /** Used by an account-level "manage cookies" surface to re-prompt
   *  the banner. Not currently wired into any page but exposed so
   *  later GDPR-rights flows can call it. */
  revoke(): void {
    this.write(null);
  }

  private read(): ConsentChoice {
    if (!isPlatformBrowser(this.platformId)) return null;
    return this.store.read(STORAGE_IDB_KEY) ?? null;
  }

  private write(choice: ConsentChoice): void {
    if (isPlatformBrowser(this.platformId)) {
      if (choice === null) void this.store.delete(STORAGE_IDB_KEY);
      else void this.store.write(STORAGE_IDB_KEY, choice);
    }
    this._choice$.next(choice);
  }
}
